import { inRoundEntities } from "./foundationEntities";

/**
 * Which Foundation Data v2 year an AI Review should default to.
 *
 * A round is a PERIOD, not a year — `IssbQuestionnaire` carries `startYear`/`startMonth` and
 * `endYear`/`endMonth`, both nullable — while foundation data is keyed by a single calendar year. So the
 * mapping is a decision rather than a lookup, and it lives here so it can be stated once and tested.
 */

/**
 * The year to preselect, or null when there is nothing to choose.
 *
 * `endYear`, not `startYear`: a round covering Apr 2024 – Mar 2025 reports ON 2025, and a reporter filling it
 * in wants that year's approved figures. Falling back to the most recent year that exists matters more than
 * it looks — `endYear` is nullable, and an organisation may simply not have created that year in v2 yet, in
 * which case an empty selection is something the user has to notice before the feature does anything.
 *
 * @param {Array<{year: number}>} years the org's foundation years, any order
 * @param {{endYear?: number}|null|undefined} round
 * @returns {number|null}
 */
export function defaultFoundationYear(years, round) {
  const available = (years ?? []).filter((y) => y && Number.isFinite(y.year));
  if (available.length === 0) return null;
  const exact = available.find((y) => y.year === round?.endYear);
  if (exact) return exact.year;
  // Most recent, computed rather than assuming the caller sorted: the year list arrives from the API in
  // whatever order it likes, and a "latest" that depended on that would be right until it wasn't.
  return available.reduce((latest, y) => (y.year > latest.year ? y : latest)).year;
}

/**
 * Collapses the per-workstream year rows into one row per calendar year.
 *
 * Since ESG-4003 a reporting year holds one `foundation_data_year` row per workstream — GHG Emissions, Risks
 * and Opportunities, Targets — so a raw render produced three chips all labelled "2026", each showing its own
 * row count, where clicking any one selected all three. They were never three choices: `foundation_year_ids`
 * carries YEARS, and the agent-scoped read aggregates every workstream for a year on purpose, so picking 2026
 * has always meant all of 2026.
 *
 * So this is what the picker actually offers: one year, the total figures it would contribute, and the
 * breakdown that says where they come from. A workstream with no approved figures is kept in the breakdown
 * rather than hidden — "Targets 0" is the answer to "why did the targets questions get nothing", and an absent
 * line is not.
 *
 * Given `{ round }`, it counts only that round's entities (every entity when it names none) and the APPROVED
 * figures (`publishedRowCount`) — exactly what AI Review drafts from (ESG-4019). Before, the chip showed the
 * organisation's whole working set: "2026 · 368" on a subsidiary's round that would receive none, so the draft
 * came back with no foundation citations and nothing had said why. A year whose rows are all another entity's
 * stays listed, at 0, so the reader can see it is empty for THIS round rather than wonder where it went.
 * `curatedRowCount` keeps its name on the output; it is the count the AI would get.
 *
 * @param {Array<{id: number, year: number, questionGroup?: string, entityId?: string, curatedRowCount?: number,
 *          publishedRowCount?: number}>} years
 * @param {{round?: {entities?: string[]}}} [scope]
 * @returns {Array<{year: number, curatedRowCount: number, workstreams: Array<{questionGroup: string, curatedRowCount: number}>}>}
 *          newest year first; workstreams in the order the API listed them
 */
export function groupFoundationYears(years, { round } = {}) {
  const byYear = new Map();
  (years ?? []).forEach((row) => {
    if (!row || !Number.isFinite(row.year)) return;
    const inScope = inRoundEntities(round, row.entityId);
    // Published when the server sends it; the working-set count otherwise, so an older backend still renders.
    const raw = Number.isFinite(row.publishedRowCount) ? row.publishedRowCount : row.curatedRowCount;
    const count = inScope && Number.isFinite(raw) ? raw : 0;
    const existing = byYear.get(row.year);
    const workstream = { questionGroup: row.questionGroup ?? "", curatedRowCount: count };
    if (existing) {
      existing.curatedRowCount += count;
      // One line per workstream, summed across entities: a year holds each workstream once PER ENTITY.
      const same = existing.workstreams.find((w) => w.questionGroup === workstream.questionGroup);
      if (same) same.curatedRowCount += count;
      else existing.workstreams.push(workstream);
    } else {
      byYear.set(row.year, {
        year: row.year,
        curatedRowCount: count,
        workstreams: [workstream],
      });
    }
  });
  return [...byYear.values()].sort((a, b) => b.year - a.year);
}

/**
 * The `foundation_year_ids` the AI Assistant chat sends (ESG-4020): the round's reporting year, by the same rule
 * as the AI Review card and the answer editor's picker, so all three agree on which approved figures are in
 * play. Empty when the chat has no round, the module is off, or there is no year to send — and an empty list is
 * what keeps the chat exactly as it was, since the AI service fetches nothing without a year.
 *
 * Which entities' figures is decided server-side from the round the chat sends as `active_round_ids`.
 *
 * @param {Array<{year: number}>|undefined} years the org's foundation year rows
 * @param {{endYear?: number}|null|undefined} round the round the chat is open on
 * @param {{enabled?: boolean}} [options] false when the Foundation Data module is off
 * @returns {string[]}
 */
export function chatFoundationYearIds(years, round, { enabled = true } = {}) {
  if (!enabled || !round) return [];
  const year = defaultFoundationYear(years, round);
  return year == null ? [] : [String(year)];
}
